import { Inject, Injectable, PLATFORM_ID, WritableSignal, inject, signal } from '@angular/core';
import { Location, isPlatformBrowser } from '@angular/common';
import { catchError, finalize, of, tap, timeout } from 'rxjs';
import { environment } from '../../environments/environment';
import { ModalContentService } from '../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../shared/constants/modal-content-types';
import { ERROR_REASONS, HTTP_STATUS_CODES, isSessionEndedResponse } from '../shared/constants/errors';
import { LocalStorageService } from '../shared/local-storage.service';
import { LOCALSTORAGE_KEYS } from '../shared/constants/databases';
import { ApiService } from '../shared/api.service';
import { RequestHandlerParams } from '../shared/types/api-service.types';
import { ApiResponse } from '../shared/common/types';
import { AccountService } from '../shared/account.service';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NotificationService } from '../shared/notification/notification.service';
import { KlaviyoService } from '../shared/klaviyo/klaviyo.service';
import { Session } from '../shared/types/session.type';
import { AttributionService } from '../shared/attribution/attribution.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private localStorageService = inject(LocalStorageService);
  private apiService = inject(ApiService);
  private accountService = inject(AccountService);
  #router = inject(Router);
  #notificationService = inject(NotificationService);
  #klaviyoService = inject(KlaviyoService);
  #modalContentService = inject(ModalContentService);
  #attributionService = inject(AttributionService);


  endpoints = {
    login: `/login`,
    logout: `/logout`
  }

  isLoadingLogin: WritableSignal<boolean> = signal(false);

  constructor(
    private location: Location,
    @Inject(PLATFORM_ID) private platformId: any
  ) { }


  async login(email: string, password: string, hasExtendedSession: boolean = false) {
    this.isLoadingLogin.set(true)

    const params: RequestHandlerParams = {
      endpoint: this.endpoints.login,
      method: 'POST',
      body: this.#attributionService.withAttribution({ email, password, hasExtendedSession }),
      apiV3: true,
      // Errors come back rather than being surfaced by the interceptor, because
      // a failed login is the first place an explorer is recognised: they have
      // an account but no password, so the sign-in they just tried could never
      // have worked (DEV-11262). Anything that is not that is reported exactly
      // as before — see #handleLoginError.
      returnError: true,
      showErrorMessage: false
    }

    this.localStorageService.set(LOCALSTORAGE_KEYS.NO_PASSWORD_USER_EMAIL, email);

    this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap(async (response: any) => {
        if (response.error) {
          await this.#handleLoginError(response, email);
          return;
        }

        if (response.noPassword) {
          this.location.replaceState('/account-recovery/new-ecommerce');
          window.location.reload();
          return;
        }

        this.localStorageService.remove(LOCALSTORAGE_KEYS.NO_PASSWORD_USER_EMAIL);
        const { data } = response;
        this.localStorageService.set(LOCALSTORAGE_KEYS.SESSION, { ...data });

        await this.#klaviyoService.identiyUser();

        this.localStorageService.set(LOCALSTORAGE_KEYS.FROM_LOGIN, true);

        // Validate if exists a stored route to navigate after login:
        const routeToNavigateAfterLogin: string = this.localStorageService.get(LOCALSTORAGE_KEYS.ROUTE_TO_NAVIGATE_AFTER_LOGIN) || '/shop';
        this.localStorageService.remove(LOCALSTORAGE_KEYS.ROUTE_TO_NAVIGATE_AFTER_LOGIN);

        // // Get memebership permissions:
        this.accountService.getMembershipPermissions(routeToNavigateAfterLogin).subscribe();
      }),
      finalize(() => this.isLoadingLogin.set(false))
    ).subscribe();
  }

  //#region Explorer flow (DEV-11262)

  /**
   * A login that failed. Everything is reported as it always was, except the
   * one case worth a second look: the backend says this email belongs to an
   * explorer who never finished registering. They never set a password, so
   * "user not found" is technically true and completely useless to them — it
   * is the exact message that confused a customer into giving up (DEV-9943).
   */
  async #handleLoginError(response: any, email: string) {
    if (
      environment.config.flows.explorer.enabled &&
      response.reason === ERROR_REASONS.EXPLORER_NOT_ACTIVATED
    ) {
      this.#showExplorerRegistrationModal(email);
      return;
    }

    if (this.#shouldReportLoginError(response)) this.#notificationService.show({ text: response.message, type: 'error' });
  }

  /**
   * Whether a failed login is worth a message of its own.
   *
   * Asking for the error back means the shared handler no longer shows it, so
   * the cases it used to stay quiet about have to stay quiet here too. Each one
   * is already taking the customer somewhere — a maintenance page, a reload, a
   * cleared session — and a toast on the way out is noise at best, and at worst
   * contradicts the screen they are about to land on.
   */
  #shouldReportLoginError(response: any): boolean {
    const silentStatuses: number[] = [
      HTTP_STATUS_CODES.SERVER_ERROR,   // switches the app into maintenance
      HTTP_STATUS_CODES.UNKNOWN_ERROR,  // never reached the server
      HTTP_STATUS_CODES.I_AM_A_TEAPOT,  // already redirecting home
      HTTP_STATUS_CODES.SUSPENDE_USER   // suspension has its own handling
    ];

    if (silentStatuses.includes(response.status)) return false;

    return !isSessionEndedResponse(response.status, response.reason);
  }

  #showExplorerRegistrationModal(email: string) {
    this.#modalContentService.openModal(
      ModalContentTypes.CONFIRMATION,
      {
        title: 'Almost There!',
        textContent: `
          You've already started shopping with us, but your account isn't finished yet.
          Complete your registration to place your order.
        `,
        confirmButtonText: 'Complete Registration',
        cancelButtonText: 'Use a Different Email'
      }
    ).closed.subscribe((result: { confirm: boolean } | null) => {
      // "Complete Registration" has to end up at the signup. Resuming only
      // hands the session back; without this the modal closes and leaves them
      // on the login, which is the one page that cannot help them.
      if (result?.confirm) this.resumeExplorer(email, '/signup');
    });
  }

  /**
   * Picks an explorer's basket back up.
   *
   * There is nothing to restore. The backend never hands back the address it
   * held — it replaces it with whatever the next call sends — so resuming means
   * asking for the address and the delivery day again and creating the explorer
   * afresh. That returns a new token and the carts that are still open.
   *
   * The email is the one thing we can carry across, through `PRELOADED_EMAIL`,
   * which the check-address modal reads on open.
   */
  resumeExplorer(email: string, redirectTo?: string) {
    this.localStorageService.set(LOCALSTORAGE_KEYS.PRELOADED_EMAIL, email);
    this.localStorageService.remove(LOCALSTORAGE_KEYS.NO_PASSWORD_USER_EMAIL);

    this.#modalContentService.openModal(ModalContentTypes.CHECK_ADDRESS, {
      title: 'Welcome back!',
      textContent: `Confirm where you'd like your order delivered and we'll bring your basket back.`,
      closeable: true
    }).closed.subscribe((result: { explorerAccountCreated?: boolean } | null) => {
      // Only on success: a visitor who closed the modal without resuming is
      // left where they were, which is what closing it means.
      if (redirectTo && result?.explorerAccountCreated) this.#router.navigate([redirectTo]);
    });
  }

  //#endregion

  /**
   * How long to wait for the server to end the session before signing out locally
   * anyway. The call is worth making -- it is what stops a token being replayed
   * after the customer has left -- but nobody should be held on the page because
   * the API is slow or down.
   */
  private readonly logoutTimeoutMs = 4000;

  /**
   * Ends the session on the server as well as in the browser.
   *
   * `POST /logout` answers 204 and invalidates the token it was called with, so a
   * copy taken from storage stops working the moment someone signs out. The browser
   * is then cleared whatever the call did: a request that fails, times out or never
   * leaves must never be the reason somebody stays signed in.
   */
  logout() {
    if (!isPlatformBrowser(this.platformId)) return;

    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    const token = session?.accountInfo?.token;

    // No token means there is no server-side session to end, and asking anyway
    // answers 403 SESSION_MISSING -- which the error handler reads as an expired
    // session and acts on, clearing storage and reloading a second time.
    if (!token) {
      this.clearBrowserSession();
      return;
    }

    const params: RequestHandlerParams = {
      endpoint: this.endpoints.logout,
      method: 'POST',
      apiV3: true,
      // 204, so there is no body to wait for. The failure stays silent on purpose:
      // the customer asked to be signed out, not to be told the API was unreachable.
      isVoid: true,
      showErrorMessage: false,
      returnError: true
    };

    this.apiService.handleRequest<unknown>(params).pipe(
      timeout(this.logoutTimeoutMs),
      catchError(() => of(null)),
      // Runs on the 204, on an error and on the timeout alike.
      finalize(() => this.clearBrowserSession())
    ).subscribe();
  }

  private clearBrowserSession() {
    localStorage.clear();
    const cookiesToDelete = ['__kla_id'];

    cookiesToDelete.forEach(cookie => {
      document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    this.reloadPage('/');
  }

  private reloadPage(route: string) {
    this.location.replaceState(route);
    window.location.reload();
  }

  loginAsAdmin(token: string) {
    this.localStorageService.clear();

    const params: RequestHandlerParams = {
      endpoint: '/login-admin',
      method: 'GET',
      apiV3: true,
      returnError: true,
      token
    };

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap(async res => {
        if (res.error) {
          this.#notificationService.show({ text: res.message ?? 'There was an error trying to authenticate the request', type: 'error' })
          this.#router.navigate(['/']);
          return;
        }
        const { data } = res;
        this.localStorageService.set(LOCALSTORAGE_KEYS.SESSION, { ...data });
        await this.#klaviyoService.identiyUser();
        this.accountService.getMembershipPermissions('/shop').subscribe();
      })
    );
  }
}
